import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import {
  Download,
  Plus,
  TrendingUp,
  Search,
  Eye,
  Printer,
  Calendar,
  MoreVertical,
  Trash2,
  Pencil,
  SlidersHorizontal as Filters,
  Loader2,
  X,
  Filter,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
  IndianRupee,
  Activity,
  LayoutDashboard
} from 'lucide-react';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Pagination from '../../components/Common/Pagination/Pagination';
import AddVendorPaymentDrawer from './AddVendorPaymentDrawer';
import styles from './VendorPayments.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const VendorPayments = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const mainContentRef = React.useRef(null);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [editingPayment, setEditingPayment] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [summary, setSummary] = useState({
    total_month: 0,
    total_paid: 0,
    outstanding: 0,
    advance_count: 0
  });
  const [filters, setFilters] = useState({
    project_id: '',
    vendor_id: '',
    status: ''
  });
  const [filterData, setFilterData] = useState({
    projects: [],
    vendors: []
  });

  useEffect(() => {
    fetchFilterData();
    fetchSummary();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPayments();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage, itemsPerPage, filters]);

  const fetchFilterData = async () => {
    try {
      const [projectsRes, vendorsRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/vendors-dropdown')
      ]);
      setFilterData({
        projects: projectsRes.data.data || [],
        vendors: vendorsRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/vendor-payments/summary');
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await api.get('/vendor-payments', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          ...filters
        }
      });

      if (response.data.success) {
        setPayments(response.data.data.data);
        setTotalItems(response.data.data.total);
        if (response.data.summary) {
          setSummary(response.data.summary);
        }
      }
    } finally {
      // hideLoader() is called by interceptor
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      project_id: '',
      vendor_id: '',
      status: ''
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.project_id || filters.vendor_id || filters.status !== '';

  const onPaymentSuccess = () => {
    fetchPayments();
    fetchSummary();
    setCurrentPage(1);
    setEditingPayment(null);
  };

  const handleEditClick = (payment) => {
    setEditingPayment(payment);
    setIsDrawerOpen(true);
    setOpenMenuIndex(null);
  };

  const handleAddClick = () => {
    setEditingPayment(null);
    setIsDrawerOpen(true);
  };

  const handleDelete = async (payment) => {
    setOpenMenuIndex(null);

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Payment</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Record?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete payment <strong>#PAY-${payment.id}</strong>?<br/>
          This action cannot be undone.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/vendor-payments/${payment.id}`);
          if (response.data.success) {
            toast.success('Payment deleted successfully');
            fetchPayments();
            fetchSummary();
          }
        } catch (error) {
          console.error('Error deleting payment:', error);
          toast.error(error.response?.data?.message || 'Failed to delete payment');
        }
      }
    });
  };

  const getPaymentModeLabel = (mode) => {
    const modes = {
      1: 'Bank Transfer',
      2: 'Cheque',
      3: 'Cash',
      4: 'RTGS/NEFT'
    };
    return modes[mode] || 'Other';
  };

  const toggleActionMenu = (index, e, payment) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(index);
      setSelectedPayment(payment);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const summaryData = [
    {
      label: "Total This Month",
      value: `₹${summary.total_month.toLocaleString('en-IN')}`,
      type: "positive",
      style: styles.blue,
      icon: <Activity size={20} />,
      subtext: "Payments recorded this month"
    },
    {
      label: "Total Paid",
      value: `₹${summary.total_paid.toLocaleString('en-IN')}`,
      type: "normal",
      style: styles.green,
      icon: <CheckCircle2 size={20} />,
      subtext: "Cumulative payments"
    },
    {
      label: "Outstanding",
      value: `₹${summary.outstanding.toLocaleString('en-IN')}`,
      type: "normal",
      style: styles.orange,
      icon: <IndianRupee size={20} />,
      subtext: "Remaining balance"
    },
    {
      label: "Advance Payments",
      value: summary.advance_count.toString(),
      type: "warning",
      style: styles.purple,
      icon: <Clock size={20} />,
      subtext: "Total advance records"
    }
  ];

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create vendor payments');
  const canEdit = isAdmin || userPermissions.includes('edit vendor payments');
  const canDelete = isAdmin || userPermissions.includes('delete vendor payments');

  return (
    <div className={`${styles['vendor-payments-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Vendor Payments</h1>
              <p>Track and manage your vendor transactions and outstanding balances.</p>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['control-btn']}>
                <Download size={18} />
                <span>Export</span>
              </button>
              {canCreate && (
                <button className={styles['add-btn']} onClick={handleAddClick}>
                  <Plus size={20} />
                  <span>Add Payment</span>
                </button>
              )}
            </div>
          </div>

          {/* Summary — collapsible on mobile */}
          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}> Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {summaryData[0]?.value} this month · {summaryData[2]?.value} outstanding
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {summaryData.map((m, i) => (
                <div key={i} className={`${styles['metric-card']} ${m.style} ${m.type === 'positive' ? styles['highlight-card'] : ''}`}>
                  <div className={styles['metric-icon-box']}>
                    {m.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{m.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{m.value}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search LOI or Vendor..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(Number(option.value)); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {hasActiveFilters && <span className={styles['filter-badge']}></span>}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Transactions</h3>
                  <p>Filter records by project, vendor, or payment status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(d => d.id === filters.project_id)?.project_name
                    } : null}
                    onChange={(opt) => handleFilterChange('project_id', opt ? opt.value : '')}
                    options={filterData.projects.map(d => ({ value: d.id, label: d.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Vendor</label>
                  <CustomSelect
                    value={filters.vendor_id ? {
                      value: filters.vendor_id,
                      label: filterData.vendors.find(d => d.id === filters.vendor_id)?.vendor_name
                    } : null}
                    onChange={(opt) => handleFilterChange('vendor_id', opt ? opt.value : '')}
                    options={filterData.vendors.map(v => ({ value: v.id, label: v.vendor_name }))}
                    placeholder="All Vendors"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Payment Status</label>
                  <CustomSelect
                    value={filters.status ? {
                      value: filters.status,
                      label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1)
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={[
                      { value: 'advance', label: 'Advance' },
                      { value: 'partial', label: 'Partial' },
                      { value: 'final', label: 'Final' }
                    ]}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            <div className={styles['table-wrapper']}>
              <table className={styles['payment-table']}>
                <thead>
                  <tr>
                    <th>PAYMENT ID</th>
                    <th>DATE</th>
                    <th>PROJECT</th>
                    <th>VENDOR</th>
                    <th>LOI# / INVOICE</th>
                    <th>MODE</th>
                    <th>AMOUNT PAID</th>
                    <th>BALANCE</th>
                    <th>STATUS</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    !loading && <NoData message="No payments found." colSpan={10} isTable={true} />
                  ) : (
                    payments.map((payment, index) => (
                      <tr key={payment.id}>
                        <td className={styles['id-cell']}>
                          <div className={styles['receipt-info']}>
                            <span className={styles['receipt-text']}>#PAY-{payment.id}</span>
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {new Date(payment.payment_date).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className={styles['project-name']}>
                          {payment.project?.project_name || 'N/A'}
                          <span className={styles['small-id']}>{payment.project?.project_code}</span>
                        </td>
                        <td className={styles['vendor-name']}>{payment.vendor?.vendor_name}</td>
                        <td className={styles['po-inv-cell']}>
                          <span className={styles['po-text']}>{payment.purchase_order?.po_number}</span>
                          <span className={styles['inv-sub']}>₹{(parseFloat(payment.purchase_order?.total_amount || 0)).toLocaleString("en-IN")}</span>
                        </td>
                        <td>{getPaymentModeLabel(payment.payment_mode)}</td>
                        <td className={styles['amount-cell']}>₹{parseFloat(payment.amount).toLocaleString('en-IN')}</td>
                        <td className={styles['balance-cell']}>
                          ₹{parseFloat(payment.balance || 0).toLocaleString('en-IN')}
                        </td>
                        <td>
                          <span className={`${styles['status-pill']} ${payment.status === 'final' ? styles['active'] : styles['inactive']}`}>
                            {payment.status.toUpperCase()}
                          </span>
                        </td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                              onClick={(e) => toggleActionMenu(index, e, payment)}
                            >
                              <MoreVertical size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles['payment-cards']}>
              {payments.length === 0 ? (
                !loading && <NoData message="No payments recorded yet." isTable={false} />
              ) : (
                payments.map((payment, index) => (
                  <div key={payment.id} className={styles['payment-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']}>#PAY-{payment.id}</span>
                        <span className={styles['card-amount']}>₹{parseFloat(payment.amount).toLocaleString('en-IN')}</span>
                      </div>
                      <div className={styles['action-wrapper']}>
                        <button
                          className={styles['action-btn']}
                          onClick={(e) => toggleActionMenu(index, e, payment)}
                        >
                          <MoreVertical size={18} />
                        </button>
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Vendor & Project</span>
                        <span className={styles['detail-value']}>
                          {payment.vendor?.vendor_name}
                          <span style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                            {payment.project?.project_name || 'Generic Project'}
                          </span>
                        </span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Payment Mode</span>
                        <span className={styles['detail-value']}>{getPaymentModeLabel(payment.payment_mode)}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Balance</span>
                        <span className={styles['detail-value']} style={{ color: '#ef4444' }}>
                          ₹{(parseFloat(payment.purchase_order?.total_amount || 0) - (parseFloat(payment.purchase_order?.advance_paid_amount || 0) + parseFloat(payment.purchase_order?.final_paid || 0))).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <div className={styles['footer-left']}>
                        <Calendar size={14} className={styles['text-muted']} />
                        <span>{new Date(payment.payment_date).toLocaleDateString('en-IN')}</span>
                      </div>
                      <span className={`${styles['status-pill']} ${payment.status === 'final' ? styles['active'] : styles['inactive']}`}>
                        {payment.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => {
                setCurrentPage(page);
                if (mainContentRef.current) mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </div>

          {/* FAB Add Button for Mobile */}
          {canCreate && (
            <button className={styles['fab-add-btn']} onClick={handleAddClick}>
              <Plus size={24} />
            </button>
          )}

          {/* Global Fixed Action Menu */}
          {openMenuIndex !== null && (
            <div
              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 70
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => navigate(`/payment/vendor-payments/${selectedPayment?.id}`)}>
                <Eye size={16} /> View
              </button>
              <button onClick={() => { }}>
                <Printer size={16} /> Print Receipt
              </button>
              <div className={styles['dropdown-divider']}></div>
              {canEdit && (
                <button onClick={() => handleEditClick(selectedPayment)}>
                  <Pencil size={16} /> Edit
                </button>
              )}
              {canDelete && (
                <button className={[styles['dropdown-item'], styles['deselect']].join(' ')} onClick={() => handleDelete(selectedPayment)}>
                  <Trash2 size={16} /> Delete
                </button>
              )}
            </div>
          )}

          <Footer />
        </main>
      </div>

      <AddVendorPaymentDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditingPayment(null);
        }}
        onSuccess={onPaymentSuccess}
        payment={editingPayment}
      />
    </div>
  );
};

export default VendorPayments;
